import { Node } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    scriptRow: {
      insertScriptRowAfter: () => ReturnType;
      deleteCurrentScriptRow: () => ReturnType;
    };
  }
}

export const ScriptRow = Node.create({
  name: 'scriptRow',
  group: 'block',
  content: 'scriptCell scriptCell',
  isolating: true,
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-type="script-row"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      { 'data-type': 'script-row', class: 'script-row', ...HTMLAttributes },
      0,
    ];
  },

  /**
   * NodeView gives us a hover-revealed × button per row, only when the editor
   * is editable. Wraps content in .script-row-content so the delete button
   * can sit absolute-positioned outside the grid without disturbing the
   * audio/visual columns. Read-only contexts (client review, diff view,
   * compare panes) skip the button entirely.
   */
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'script-row';
      wrapper.setAttribute('data-type', 'script-row');

      const contentDOM = document.createElement('div');
      contentDOM.className = 'script-row-content';
      wrapper.appendChild(contentDOM);

      if (editor.isEditable) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'script-row-delete';
        btn.setAttribute('contenteditable', 'false');
        btn.setAttribute('aria-label', 'Delete this row');
        btn.title = 'Delete this row';
        btn.innerHTML =
          '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>';
        // Prevent the click from being absorbed as an editor selection event.
        btn.addEventListener('mousedown', (e) => e.preventDefault());
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const pos = typeof getPos === 'function' ? getPos() : null;
          if (pos == null) return;
          // The view can survive text edits, so its original node size is stale.
          const live = editor.state.doc.nodeAt(pos);
          if (!live || live.type.name !== 'scriptRow') return;
          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + live.nodeSize })
            .run();
        });
        wrapper.appendChild(btn);
      }

      return {
        dom: wrapper,
        contentDOM,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false;
          node = updatedNode;
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertScriptRowAfter:
        () =>
        ({ tr, state, dispatch, editor }) => {
          const { $from } = state.selection;
          let depth = $from.depth;
          while (depth > 0 && $from.node(depth).type.name !== 'scriptRow') depth--;
          if (depth === 0) return false;

          const insertPos = $from.after(depth);
          const schema = editor.schema;
          const newRow = schema.nodes.scriptRow!.create(null, [
            // New rows default to Voiceover and B-roll; existing untagged
            // rows stay untyped (null). See src/shared/{audio,visual}Types.ts.
            schema.nodes.scriptCell!.create({ kind: 'audio', audioType: 'voiceover' }, [
              schema.nodes.paragraph!.create(),
            ]),
            schema.nodes.scriptCell!.create({ kind: 'visuals', visualType: 'broll' }, [
              schema.nodes.paragraph!.create(),
            ]),
          ]);
          if (dispatch) {
            tr.insert(insertPos, newRow);
            tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 3)));
            dispatch(tr);
          }
          return true;
        },

      deleteCurrentScriptRow:
        () =>
        ({ tr, state, dispatch }) => {
          const { $from } = state.selection;
          let depth = $from.depth;
          while (depth > 0 && $from.node(depth).type.name !== 'scriptRow') depth--;
          if (depth === 0) return false;
          const from = $from.before(depth);
          const to = $from.after(depth);
          if (dispatch) {
            tr.delete(from, to);
            dispatch(tr);
          }
          return true;
        },
    };
  },
});
